import { Box, Column } from '@tempmee/react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { routes } from '~/routes';
import { AuthBackLink } from '../shared';
import { ChooseMethodStep, type TForgotPasswordMethod } from './chooseMethod/ChooseMethodStep';
import { ResetPasswordStep } from './resetPassword/ResetPasswordStep';
import { ResetSuccessModal } from './resetPassword/ResetSuccessModal';

type TStep = 1 | 2;

const BACK_LINK_LEFT = 26;
const CHROME_TOP = 40;
const FORM_PADDING_BOTTOM = 80;
const PAGE_SIDE_PADDING = 24;

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<TStep>(1);
  const [identifier, setIdentifier] = useState<string>('');
  const [method, setMethod] = useState<TForgotPasswordMethod>('email');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleCodeSent = ({
    identifier: nextIdentifier,
    method: nextMethod,
  }: {
    identifier: string;
    method: TForgotPasswordMethod;
  }) => {
    setIdentifier(nextIdentifier);
    setMethod(nextMethod);
    setStep(2);
  };

  const handleResetSuccess = () => {
    setShowSuccessModal(true);
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    // Pre-fill the login email when the user came in via the email method so
    // they don't re-type it after the reset flow.
    const prefillEmail = method === 'email' ? identifier : undefined;
    navigate(routes.signUpRedesign.login, { state: { prefillEmail } });
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setIdentifier('');
  };

  return (
    <Column
      style={{ flex: 1, position: 'relative' }}
      data-testid="forgot-password-page"
    >
      {/* Absolute so the title sits on the same horizontal line as the back button. */}
      <Box style={{ position: 'absolute', top: CHROME_TOP, left: BACK_LINK_LEFT }}>
        {step === 1 ? (
          <AuthBackLink to={routes.signUpRedesign.login} />
        ) : (
          <AuthBackLink onClick={handleBackToStep1} />
        )}
      </Box>

      <Box
        style={{
          paddingLeft: PAGE_SIDE_PADDING,
          paddingRight: PAGE_SIDE_PADDING,
          paddingTop: CHROME_TOP,
          paddingBottom: FORM_PADDING_BOTTOM,
          flex: 1,
        }}
      >
        {step === 1 ? (
          <ChooseMethodStep onCodeSent={handleCodeSent} />
        ) : (
          <ResetPasswordStep
            identifier={identifier}
            onResetSuccess={handleResetSuccess}
          />
        )}
      </Box>

      <ResetSuccessModal isOpen={showSuccessModal} onClose={handleSuccessClose} />
    </Column>
  );
};
